import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { recommendationService } from "../../api/recommendations";
import jobPostingService from "../../api/jobPosting";
import apiClient from "../../api/axiosConfig";
import Button from "../../components/common/Button";
import Swal from "sweetalert2";

// MUI
import { useTheme } from "@mui/material/styles";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";

import { AlertCircle, Users, Briefcase, MessageSquare, ClipboardList } from "lucide-react";

/* ────────────── constants ────────────── */
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
        width: 320,
      },
    },
  },
  /* keep dropdown open while typing in search */
  autoFocus: false,
};

const STEPS = [
  { id: 1, label: "Select Professionals", icon: Users },
  { id: 2, label: "Choose Jobs",          icon: Briefcase },
  { id: 3, label: "Add Message",          icon: MessageSquare },
  { id: 4, label: "Review",               icon: ClipboardList },
];

/* ────────────── component ────────────── */
export default function SendRecommendationPage() {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const [step, setStep] = useState(1);

  /* professionals */
  const [allProfessionals, setAllProfessionals] = useState([]);
  const [selectedProfIds, setSelectedProfIds]   = useState([]);
  const [sendToAll, setSendToAll]               = useState(false);
  const [profSearch, setProfSearch]             = useState("");
  const [loadingProfs, setLoadingProfs]         = useState(true);

  /* jobs */
  const [allJobs, setAllJobs]           = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [jobSearch, setJobSearch]           = useState("");
  const [loadingJobs, setLoadingJobs]       = useState(true);

  /* misc */
  const [message,    setMessage]    = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");

  /* ── fetch on mount ── */
  useEffect(() => {
    (async () => {
      try {
        const jobsRes = await jobPostingService.getMyJobPostings();
        setAllJobs(jobsRes.data?.results ?? jobsRes.data ?? []);
        setLoadingJobs(false);

        const profRes = await apiClient.get("/users/profile/professional/list/");
        setAllProfessionals(profRes.data?.results ?? profRes.data ?? []);
        setLoadingProfs(false);
        setError("");
      } catch {
        setError("Failed to load professionals or jobs. Please try again.");
        setLoadingProfs(false);
        setLoadingJobs(false);
      }
    })();
  }, []);

  /* ── filtered lists ── */
  const filteredProfs = allProfessionals.filter((p) =>
    `${p.first_name} ${p.last_name} ${p.email}`
      .toLowerCase()
      .includes(profSearch.toLowerCase())
  );

  const filteredJobs = allJobs.filter((j) =>
    (j.title ?? "").toLowerCase().includes(jobSearch.toLowerCase())
  );

  /* ── helpers ── */
  const profById = (id) => allProfessionals.find((p) => p.id === id);
  const jobById  = (id) => allJobs.find((j) => j.id === id);

  const activeRecipientIds = sendToAll
    ? allProfessionals.map((p) => p.id)
    : selectedProfIds;

  const itemWeight = (id, selected) => ({
    fontWeight: selected.includes(id)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  });

  /* unselect a single prof chip */
  const removeProfChip = useCallback((e, id) => {
    e.stopPropagation();
    setSelectedProfIds((prev) => prev.filter((x) => x !== id));
  }, []);

  /* unselect a single job chip */
  const removeJobChip = useCallback((e, id) => {
    e.stopPropagation();
    setSelectedJobIds((prev) => prev.filter((x) => x !== id));
  }, []);

  /* ── navigation ── */
  const handleNext = () => {
    setError("");
    if (step === 1 && activeRecipientIds.length === 0) {
      setError("Please select at least one professional, or enable 'Send to All'.");
      return;
    }
    if (step === 2 && selectedJobIds.length === 0) {
      setError("Please select at least one job.");
      return;
    }
    if (step === 3 && message.trim().length < 10) {
      setError("Message must be at least 10 characters.");
      return;
    }
    setStep((s) => s + 1);
  };

  const handlePrevious = () => {
    setError("");
    setStep((s) => s - 1);
  };

  /* ── submit ── */
  const handleSubmit = async () => {
    if (!activeRecipientIds.length || !selectedJobIds.length || !message.trim()) {
      setError("Please complete all steps.");
      return;
    }
    setSubmitting(true);
    setError("");

    try {
      for (const jobId of selectedJobIds) {
        await recommendationService.createRecommendation({
          professional_ids: activeRecipientIds,
          job_id: jobId,
          message,
        });
      }

      const recipientNames = activeRecipientIds
        .map((id) => { const p = profById(id); return p ? `${p.first_name} ${p.last_name}` : `#${id}`; })
        .join(", ");
      const jobTitles = selectedJobIds
        .map((id) => jobById(id)?.title ?? `#${id}`)
        .join(", ");

      const result = await Swal.fire({
        icon: "success",
        title: " Recommendation Sent",
        html: `
          <div style="text-align:left;font-size:0.92rem;line-height:1.75">
            <p><strong>📋 Job(s):</strong> ${jobTitles}</p>
            <p style="margin-top:10px"><strong>👥 Sent to (${activeRecipientIds.length}):</strong></p>
            <div style="
              max-height:110px;overflow-y:auto;
              border:1px solid #e5e7eb;border-radius:8px;
              padding:8px 12px;margin-top:4px;
              background:#f9fafb;color:#374151;font-size:0.88rem;line-height:1.6
            ">${recipientNames}</div>
          </div>
        `,
        confirmButtonText: "View Recommendations",
        confirmButtonColor: "#2563eb",
        showCancelButton: true,
        cancelButtonText: "Send Another",
        cancelButtonColor: "#6b7280",
      });

      if (result.isConfirmed) {
        navigate("/recruiter/recommendations");
      } else {
        setStep(1); setSelectedProfIds([]); setSelectedJobIds([]);
        setMessage(""); setSendToAll(false);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to Send",
        text: err.response?.data?.message ?? "Something went wrong. Please try again.",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ──────────────────── render ──────────────────── */
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Recommend a Job</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Select professionals and recommend job opportunities to them
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Progress steps */}
        <div className="mb-8 flex justify-between items-start">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div key={s.id} className="flex flex-col items-center flex-1 relative">
                {idx < STEPS.length - 1 && (
                  <div className={`absolute top-5 left-1/2 w-full h-0.5 ${s.id < step ? "bg-blue-500" : "bg-gray-200"}`} />
                )}
                <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${s.id <= step ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"}`}>
                  <Icon size={17} />
                </div>
                <p className="text-[11px] text-gray-500 mt-1.5 text-center leading-tight">{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-md p-8">

          {/* ══ STEP 1 — Professionals ══ */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-semibold mb-1">Select Professionals</h2>
              <p className="text-sm text-gray-400 mb-5">Pick recipients individually or blast to all professionals.</p>

              {/* Send-to-all */}
              <label className="flex items-center gap-3 mb-5 p-3.5 bg-blue-50 border border-blue-200 rounded-xl cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={sendToAll}
                  onChange={(e) => { setSendToAll(e.target.checked); if (e.target.checked) setSelectedProfIds([]); }}
                  className="w-4 h-4 accent-blue-600"
                />
                <span className="text-blue-800 font-medium text-sm">
                  Send to All Professionals
                  {allProfessionals.length > 0 && (
                    <span className="ml-1.5 text-blue-500 font-normal">({allProfessionals.length} total)</span>
                  )}
                </span>
              </label>

              {!sendToAll && (
                <>
                  {loadingProfs ? (
                    <p className="text-sm text-gray-400 py-4 text-center">Loading professionals…</p>
                  ) : allProfessionals.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No professionals available.</p>
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel id="prof-label">Select professionals</InputLabel>
                      <Select
                        labelId="prof-label"
                        multiple
                        value={selectedProfIds}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedProfIds(typeof val === "string" ? val.split(",").map(Number) : val);
                        }}
                        input={<OutlinedInput label="Select professionals" />}
                        MenuProps={MenuProps}
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                            {selected.map((id) => {
                              const p = profById(id);
                              return (
                                <Chip
                                  key={id}
                                  label={p ? `${p.first_name} ${p.last_name}` : `#${id}`}
                                  size="small"
                                  onDelete={(e) => removeProfChip(e, id)}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  sx={{ bgcolor: "#dbeafe", color: "#1e40af", "& .MuiChip-deleteIcon": { color: "#3b82f6", "&:hover": { color: "#1d4ed8" } } }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {/* ── search box inside dropdown ── */}
                        <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: "white" }}>
                          <TextField
                            size="small"
                            autoFocus
                            fullWidth
                            placeholder="Search by name or email…"
                            value={profSearch}
                            onChange={(e) => setProfSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" } }}
                          />
                        </ListSubheader>

                        {filteredProfs.length === 0 && (
                          <MenuItem disabled>
                            <span className="text-sm text-gray-400">No results</span>
                          </MenuItem>
                        )}

                        {filteredProfs.map((prof) => (
                          <MenuItem key={prof.id} value={prof.id} style={itemWeight(prof.id, selectedProfIds)}>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-800">
                                {prof.first_name} {prof.last_name}
                              </span>
                              <span className="text-xs text-gray-400">{prof.email}</span>
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {selectedProfIds.length > 0 && (
                    <p className="mt-2 text-xs text-blue-600 font-medium">
                      {selectedProfIds.length} professional{selectedProfIds.length > 1 ? "s" : ""} selected
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ STEP 2 — Jobs ══ */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-semibold mb-1">Choose Job(s) to Recommend</h2>
              <p className="text-sm text-gray-400 mb-5">Select one or more of your active job postings.</p>

              {loadingJobs ? (
                <p className="text-sm text-gray-400 py-4 text-center">Loading jobs…</p>
              ) : allJobs.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-800">
                  You have no active job postings. Please create one first.
                </div>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="job-label">Select job(s)</InputLabel>
                  <Select
                    labelId="job-label"
                    multiple
                    value={selectedJobIds}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedJobIds(typeof val === "string" ? val.split(",").map(Number) : val);
                    }}
                    input={<OutlinedInput label="Select job(s)" />}
                    MenuProps={MenuProps}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                        {selected.map((id) => {
                          const j = jobById(id);
                          return (
                            <Chip
                              key={id}
                              label={j?.title ?? `Job #${id}`}
                              size="small"
                              onDelete={(e) => removeJobChip(e, id)}
                              onMouseDown={(e) => e.stopPropagation()}
                              sx={{ bgcolor: "#dcfce7", color: "#166534", "& .MuiChip-deleteIcon": { color: "#16a34a", "&:hover": { color: "#15803d" } } }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {/* ── search box inside dropdown ── */}
                    <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: "white" }}>
                      <TextField
                        size="small"
                        autoFocus
                        fullWidth
                        placeholder="Search jobs by title…"
                        value={jobSearch}
                        onChange={(e) => setJobSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" } }}
                      />
                    </ListSubheader>

                    {filteredJobs.length === 0 && (
                      <MenuItem disabled>
                        <span className="text-sm text-gray-400">No results</span>
                      </MenuItem>
                    )}

                    {filteredJobs.map((job) => (
                      <MenuItem key={job.id} value={job.id} style={itemWeight(job.id, selectedJobIds)}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-800">{job.title}</span>
                          {job.location && (
                            <span className="text-xs text-gray-400">— {job.location}</span>
                          )}
                          {job.status && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${job.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                              {job.status}
                            </span>
                          )}
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedJobIds.length > 0 && (
                <p className="mt-2 text-xs text-green-700 font-medium">
                  {selectedJobIds.length} job{selectedJobIds.length > 1 ? "s" : ""} selected
                </p>
              )}
            </div>
          )}

          {/* ══ STEP 3 — Message ══ */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-semibold mb-1">Add a Message</h2>
              <p className="text-sm text-gray-400 mb-4">
                This message will be sent to all selected professionals.
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="E.g., 'This opportunity appears to be a great fit for your profile. Kindly review and submit your application.'"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm leading-relaxed"
                rows={6}
              />
              <p className="text-xs text-gray-400 mt-1">{message.length} / 1000 characters</p>
            </div>
          )}

          {/* ══ STEP 4 — Review ══ */}
          {step === 4 && (
            <div>
              <h2 className="text-xl font-semibold mb-6">Review & Confirm</h2>
              <div className="space-y-4">

                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-semibold text-gray-700 text-sm">
                      Recipients ({activeRecipientIds.length})
                    </h3>
                    {sendToAll && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">All Professionals</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                    {activeRecipientIds.slice(0, 30).map((id) => {
                      const p = profById(id);
                      return (
                        <span key={id} className="text-xs bg-blue-50 border border-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                          {p ? `${p.first_name} ${p.last_name}` : `#${id}`}
                        </span>
                      );
                    })}
                    {activeRecipientIds.length > 30 && (
                      <span className="text-xs text-gray-400 px-2 py-0.5">+{activeRecipientIds.length - 30} more</span>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <h3 className="font-semibold text-gray-700 text-sm mb-2">
                    Job(s) ({selectedJobIds.length})
                  </h3>
                  <ul className="space-y-1">
                    {selectedJobIds.map((id) => {
                      const j = jobById(id);
                      return (
                        <li key={id} className="text-sm text-gray-700 flex items-center gap-1.5">
                          <span className="text-green-500">•</span>
                          {j?.title ?? `Job #${id}`}
                          {j?.location && <span className="text-gray-400 text-xs">— {j.location}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <h3 className="font-semibold text-gray-700 text-sm mb-2">Message</h3>
                  <p className="text-sm text-gray-600 italic leading-relaxed">"{message}"</p>
                </div>

                <div className="text-xs text-gray-400 space-y-1 pt-1">
                  <p>✓ Email notification sent to each selected professional</p>
                  <p>✓ Dashboard notifications will be created</p>
                  {selectedJobIds.length > 1 && (
                    <p>✓ {selectedJobIds.length} recommendation records — one per job</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 gap-4">
            <Button onClick={handlePrevious} disabled={step === 1 || submitting} variant="secondary">
              Previous
            </Button>
            {step < 4 ? (
              <Button onClick={handleNext} disabled={submitting}>Next</Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} loading={submitting}>
                {submitting ? "Sending…" : "Send Recommendations"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
